import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  TextField,
  Button,
  Alert,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Tabs,
  Tab,
  Card,
  CardContent,
  CardMedia,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Person as PersonIcon,
  Favorite as FavoriteIcon,
  AdminPanelSettings as AdminIcon,
  Login as LoginIcon,
  Logout as LogoutIcon,
  PersonAdd as PersonAddIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card as CardType } from '../types';
import { useTheme } from '../contexts/ThemeContext';

const UserProfile: React.FC = () => {
  const { user, login, register, logout, isAdmin, removeSavedCard } = useAuth();
  const { mode } = useTheme();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const [authMode, setAuthMode] = useState<'signin' | 'signup'>('signin');
  const [savedCards, setSavedCards] = useState<CardType[]>([]);

  // Load user's saved cards
  useEffect(() => {
    if (user && user.savedCards.length > 0) {
      const allCards = JSON.parse(localStorage.getItem('adminCards') || '[]');
      const userSavedCards = allCards.filter((card: CardType) => 
        user.savedCards.includes(card.id)
      );
      
      // Sort by the order they appear in user.savedCards (most recent first)
      const sortedCards = userSavedCards.sort((a: CardType, b: CardType) => {
        const aIndex = user.savedCards.indexOf(a.id);
        const bIndex = user.savedCards.indexOf(b.id);
        return aIndex - bIndex; // Most recent first (lower index = more recent)
      });
      
      setSavedCards(sortedCards);
    } else {
      setSavedCards([]);
    }
  }, [user]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoggingIn(true);
    setError('');

    try {
      await login(email, password);
      if (isAdmin) {
        navigate('/admin');
      }
    } catch (err) {
      setError('Invalid email or password');
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsRegistering(true);
    setError('');

    // Validate passwords match
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      setIsRegistering(false);
      return;
    }

    // Validate password length
    if (password.length < 6) {
      setError('Password must be at least 6 characters long');
      setIsRegistering(false);
      return;
    }

    try {
      await register(email, password);
      setAuthMode('signin');
      setError('');
    } catch (err) {
      setError('Registration failed. Please try again.');
    } finally {
      setIsRegistering(false);
    }
  };

  const handleModeChange = (event: React.SyntheticEvent, newMode: 'signin' | 'signup') => {
    setAuthMode(newMode);
    setError('');
    setEmail('');
    setPassword('');
    setConfirmPassword('');
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleRemoveFromWatchlist = (cardId: string) => {
    removeSavedCard(cardId);
    setSavedCards(prev => prev.filter(card => card.id !== cardId));
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(price);
  };

  if (user) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h3" sx={{ fontWeight: 700, mb: 2 }}>
            Profile
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage your account and preferences
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {/* User Info */}
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <PersonIcon sx={{ fontSize: 40, mr: 2, color: 'primary.main' }} />
                <Box>
                  <Typography variant="h5" fontWeight="bold">
                    {user.email}
                  </Typography>
                  {isAdmin && (
                    <Chip 
                      label="ADMIN" 
                      color="error" 
                      size="small" 
                      sx={{ mt: 1, fontWeight: 'bold' }}
                    />
                  )}
                </Box>
              </Box>

              <List>
                <ListItem>
                  <ListItemIcon>
                    <FavoriteIcon />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Saved Cards" 
                    secondary={`${user.savedCards.length} cards saved`}
                  />
                </ListItem>
              </List>

              <Divider sx={{ my: 2 }} />

              <Button
                variant="outlined"
                color="error"
                startIcon={<LogoutIcon />}
                onClick={handleLogout}
                fullWidth
              >
                Sign Out
              </Button>
            </Paper>
          </Grid>

          {/* Admin Actions */}
          {isAdmin && (
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Admin Actions
                </Typography>
                <List>
                  <ListItem button onClick={() => navigate('/admin-yyd92c')}>
                    <ListItemIcon>
                      <AdminIcon />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Admin Login" 
                      secondary="Access admin panel with credentials"
                    />
                  </ListItem>
                  <ListItem button onClick={() => navigate('/management-portal/dashboard')}>
                    <ListItemIcon>
                      <FavoriteIcon />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Admin Dashboard" 
                      secondary="View site analytics and manage settings"
                    />
                  </ListItem>
                </List>
              </Paper>
            </Grid>
          )}

          {/* Watchlist Section */}
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Box>
                  <Typography variant="h5" fontWeight="bold" gutterBottom>
                    My Watchlist
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {savedCards.length > 0 
                      ? `${savedCards.length} saved cards - Most recent first`
                      : 'No cards saved yet. Start browsing the Goat Collection to save your favorite cards!'
                    }
                  </Typography>
                </Box>
                {savedCards.length > 0 && (
                  <Button
                    variant="outlined"
                    startIcon={<VisibilityIcon />}
                    onClick={() => navigate('/cards')}
                  >
                    Browse More Cards
                  </Button>
                )}
              </Box>

              {savedCards.length > 0 ? (
                <Grid container spacing={2}>
                  {savedCards.map((card) => (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={card.id}>
                      <Card 
                        sx={{ 
                          height: '100%', 
                          display: 'flex', 
                          flexDirection: 'column',
                          transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                          cursor: 'pointer',
                          '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: 4
                          }
                        }}
                        onClick={() => navigate(`/card/${card.id}`)}
                      >
                        <CardMedia
                          component="img"
                          height="200"
                          image={card.imageUrl}
                          alt={card.title}
                          onError={(e) => {
                            (e.currentTarget as HTMLImageElement).src = '/placeholder-card.jpg';
                          }}
                          sx={{ 
                            objectFit: 'contain',
                            backgroundColor: mode === 'light' ? '#f8f9fa' : '#2d3748',
                            padding: '12px',
                            borderBottom: '1px solid',
                            borderColor: 'divider'
                          }}
                        />
                        
                        <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                          <Typography variant="h6" sx={{ mb: 1, fontWeight: 600, lineHeight: 1.2 }}>
                            {card.title && card.title.length > 40 
                              ? `${card.title.substring(0, 40)}...` 
                              : card.title
                            }
                          </Typography>
                          
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="h5" sx={{ 
                              fontWeight: 700, 
                              color: mode === 'light' ? '#000000' : '#FFFFFF',
                              textShadow: '0 1px 2px rgba(0,0,0,0.1)'
                            }}>
                              {formatPrice(card.price)}
                            </Typography>
                          </Box>

                          <Box sx={{ mb: 2 }}>
                            {card.condition && (
                              <Chip 
                                label={card.condition} 
                                size="small" 
                                sx={{ mr: 1, mb: 1 }}
                              />
                            )}
                            {card.grade && (
                              <Chip 
                                label={`${card.gradingCompany} ${card.grade}`}
                                size="small" 
                                color="primary"
                                sx={{ mr: 1, mb: 1 }}
                              />
                            )}
                          </Box>

                          <Box sx={{ mt: 'auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/card/${card.id}`);
                              }}
                            >
                              View Details
                            </Button>
                            <Tooltip title="Remove from watchlist">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRemoveFromWatchlist(card.id);
                                }}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <FavoriteIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    Your watchlist is empty
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Start exploring the Goat Collection to save your favorite cards
                  </Typography>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={() => navigate('/cards')}
                  >
                    Browse Cards
                  </Button>
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ py: 4, position: 'relative' }}>
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Typography variant="h3" sx={{ fontWeight: 700, mb: 2 }}>
          {authMode === 'signin' ? 'Sign In' : 'Create Account'}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {authMode === 'signin' 
            ? 'Access your account to save cards and manage preferences'
            : 'Create a new account to start saving cards and managing preferences'
          }
        </Typography>
      </Box>

      <Paper sx={{ p: 4 }}>
        {/* Auth Mode Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs 
            value={authMode} 
            onChange={handleModeChange}
            variant="fullWidth"
            sx={{ 
              mb: -1,
              '& .MuiTab-root': {
                color: mode === 'light' ? '#666666' : '#CCCCCC',
                '&.Mui-selected': {
                  color: mode === 'light' ? '#000000' : '#FFFFFF',
                },
                '&:hover': {
                  color: mode === 'light' ? '#000000' : '#FFFFFF',
                }
              },
              '& .MuiTabs-indicator': {
                backgroundColor: mode === 'light' ? '#000000' : '#FFFFFF',
              }
            }}
          >
            <Tab 
              label="Sign In" 
              value="signin"
              icon={<LoginIcon />}
              iconPosition="start"
            />
            <Tab 
              label="Create Account" 
              value="signup"
              icon={<PersonAddIcon />}
              iconPosition="start"
            />
          </Tabs>
        </Box>

        {/* Sign In Form */}
        {authMode === 'signin' && (
          <form onSubmit={handleLogin}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              margin="normal"
              required
            />

            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}

            <Button
              type="submit"
              fullWidth
              variant="outlined"
              size="large"
              startIcon={<LoginIcon />}
              disabled={isLoggingIn}
              sx={{ 
                mt: 3,
                bgcolor: mode === 'light' ? '#000000 !important' : '#FFFFFF !important',
                color: mode === 'light' ? '#FFFFFF !important' : '#000000 !important',
                border: mode === 'light' ? '2px solid #000000 !important' : '2px solid #FFFFFF !important',
                '&:hover': {
                  bgcolor: mode === 'light' ? '#333333 !important' : '#E0E0E0 !important',
                  border: mode === 'light' ? '2px solid #333333 !important' : '2px solid #E0E0E0 !important',
                },
                '&:disabled': {
                  bgcolor: mode === 'light' ? '#CCCCCC !important' : '#666666 !important',
                  color: mode === 'light' ? '#666666 !important' : '#CCCCCC !important',
                  border: mode === 'light' ? '2px solid #CCCCCC !important' : '2px solid #666666 !important',
                }
              }}
            >
              {isLoggingIn ? 'Signing In...' : 'Sign In'}
            </Button>
          </form>
        )}

        {/* Sign Up Form */}
        {authMode === 'signup' && (
          <form onSubmit={handleRegister}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              margin="normal"
              required
              helperText="Password must be at least 6 characters long"
            />
            <TextField
              fullWidth
              label="Confirm Password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              margin="normal"
              required
            />

            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}

            <Button
              type="submit"
              fullWidth
              variant="outlined"
              size="large"
              startIcon={<PersonAddIcon />}
              disabled={isRegistering}
              sx={{ 
                mt: 3,
                bgcolor: mode === 'light' ? '#000000 !important' : '#FFFFFF !important',
                color: mode === 'light' ? '#FFFFFF !important' : '#000000 !important',
                border: mode === 'light' ? '2px solid #000000 !important' : '2px solid #FFFFFF !important',
                '&:hover': {
                  bgcolor: mode === 'light' ? '#333333 !important' : '#E0E0E0 !important',
                  border: mode === 'light' ? '2px solid #333333 !important' : '2px solid #E0E0E0 !important',
                },
                '&:disabled': {
                  bgcolor: mode === 'light' ? '#CCCCCC !important' : '#666666 !important',
                  color: mode === 'light' ? '#666666 !important' : '#CCCCCC !important',
                  border: mode === 'light' ? '2px solid #CCCCCC !important' : '2px solid #666666 !important',
                }
              }}
            >
              {isRegistering ? 'Creating Account...' : 'Create Account'}
            </Button>
          </form>
        )}
      </Paper>

      {/* Admin button - tiny and subtle in bottom right of entire page */}
      <Button 
        variant="text"
        size="small"
        href="/admin-yyd92c"
        sx={{
          position: 'fixed',
          bottom: 16,
          right: 16,
          minWidth: 'unset',
          fontSize: '0.7rem',
          color: 'rgba(0,0,0,0.3)',
          '&:hover': {
            backgroundColor: 'transparent',
            color: 'rgba(0,0,0,0.5)',
          },
          zIndex: 1000,
        }}
      >
        Admin
      </Button>
    </Container>
  );
};

export default UserProfile; 